import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer,
  Tooltip as ChartTooltip, XAxis, YAxis,
} from "recharts";
import { ExternalLink, ThumbsDown } from "lucide-react";
import { formatJamaicaDate } from "@/lib/datetime";
import {
  formatCount, formatMoney, formatMoneyShort, formatRate, getCancellations, metricsUrls, monthLabel,
  shortDate, type CancellationsResponse, type RangeParams,
} from "@/lib/metrics";
import { EmptyState, ErrorState, TableSkeleton, Tile } from "./shared";

export default function CancellationsTab({ range }: { range: RangeParams }) {
  const { data, isLoading, error } = useQuery<CancellationsResponse>({
    queryKey: [metricsUrls.cancellations(range)],
    queryFn: () => getCancellations(range),
  });

  if (isLoading) return <Card><CardContent className="pt-6"><TableSkeleton rows={8} cols={6} /></CardContent></Card>;
  if (error) return <Card><CardContent className="pt-6"><ErrorState error={error} /></CardContent></Card>;
  if (!data) return null;

  const reasonChart = data.reasons.slice(0, 12).map((r) => ({ reason: r.reason || "Not given", count: r.count, mrrLost: r.mrrLost }));
  const monthly = data.monthly.map((m) => ({ month: monthLabel(m.month), cancellations: m.cancellations, saves: m.saves, mrrLost: m.mrrLost }));

  return (
    <div className="space-y-6" data-testid="tab-cancellations">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Cancellations" value={formatCount(data.total)} sub={`${shortDate(data.from)} – ${shortDate(data.to)}`} testId="tile-cancellations" />
        <Tile label="MRR lost" value={formatMoney(data.mrrLost)} tone={data.mrrLost > 0 ? "bad" : undefined} />
        <Tile label="Avg months subscribed" value={data.avgMonthsSubscribed ? data.avgMonthsSubscribed.toFixed(1) : "–"} sub="Before they cancelled" />
        <Tile
          label="Save-offer rate"
          value={formatRate(data.offers.saveRate)}
          sub={`${formatCount(data.offers.accepted)} accepted of ${formatCount(data.offers.shown)} shown`}
          tone={data.offers.accepted > 0 ? "good" : undefined}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><ThumbsDown className="w-4 h-4" />Why they left</CardTitle>
            <CardDescription>What parents told us in the exit survey when they cancelled.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {data.reasons.length === 0 ? (
              <EmptyState title="No reasons collected" hint="Nobody filled in the exit survey in this period, or nobody cancelled at all." />
            ) : (
              <>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reasonChart} layout="vertical" margin={{ left: 10, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="reason" tick={{ fontSize: 11 }} width={140} />
                      <ChartTooltip formatter={(v: any, n: any) => [n === "mrrLost" ? formatMoney(Number(v)) : formatCount(Number(v)), n === "mrrLost" ? "MRR lost" : "Cancellations"]} />
                      <Bar dataKey="count" name="Cancellations" fill="hsl(var(--primary))" isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reason</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">Share</TableHead>
                        <TableHead className="text-right">MRR lost</TableHead>
                        <TableHead className="text-right">Avg months</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.reasons.map((r) => (
                        <TableRow key={r.reason || "none"}>
                          <TableCell className="max-w-[14rem] truncate">{r.reason || "Not given"}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatCount(r.count)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatRate(r.share)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatMoney(r.mrrLost)}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.avgMonths ? r.avgMonths.toFixed(1) : "–"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">What Stripe recorded</CardTitle>
              <CardDescription>Stripe's own cancellation reasons, including the ones we never asked about.</CardDescription>
            </CardHeader>
            <CardContent>
              {data.stripeReasons.length === 0 ? (
                <EmptyState title="Nothing from Stripe" hint="Stripe did not record a cancellation reason for anyone in this period." />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {data.stripeReasons.map((r) => (
                    <Badge key={r.reason} variant="outline" className="font-normal">
                      {r.reason || "unspecified"} · {formatCount(r.count)}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">How long they stayed</CardTitle>
              <CardDescription>Tenure at the moment they cancelled.</CardDescription>
            </CardHeader>
            <CardContent>
              {data.tenure.length === 0 ? (
                <EmptyState title="No tenure data" hint="Nobody cancelled in this period, so there is nothing to bucket." />
              ) : (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.tenure}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={40} />
                      <ChartTooltip formatter={(v: any) => [formatCount(Number(v)), "Cancellations"]} />
                      <Bar dataKey="count" fill="hsl(var(--primary))" isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Cancellations and saves by month</CardTitle>
          <CardDescription>Saves are people who took a retention offer instead of leaving.</CardDescription>
        </CardHeader>
        <CardContent>
          {monthly.length === 0 ? (
            <EmptyState title="No monthly trend yet" hint="There is not enough history in this period to draw a trend." />
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} width={40} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={70} tickFormatter={(v: number) => formatMoneyShort(v)} />
                  <ChartTooltip formatter={(v: any, n: any) => [n === "MRR lost" ? formatMoney(Number(v)) : formatCount(Number(v)), n]} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="cancellations" name="Cancellations" fill="hsl(var(--destructive))" isAnimationActive={false} />
                  <Bar yAxisId="left" dataKey="saves" name="Saves" fill="#16a34a" isAnimationActive={false} />
                  <Line yAxisId="right" type="monotone" dataKey="mrrLost" name="MRR lost" stroke="hsl(var(--foreground))" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent cancellations</CardTitle>
          <CardDescription>The last people to leave, what they said, and what it cost.</CardDescription>
        </CardHeader>
        <CardContent>
          {data.recent.length === 0 ? (
            <EmptyState title="Nobody cancelled in this period" hint="A good period. Widen the date range if you want to see older cancellations." />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">MRR lost</TableHead>
                    <TableHead className="text-right">Months</TableHead>
                    <TableHead>Offer</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recent.map((r, i) => (
                    <TableRow key={`${r.userId || r.email}-${r.at}-${i}`} data-testid={`cancellation-row-${i}`}>
                      <TableCell className="whitespace-nowrap text-sm">{formatJamaicaDate(r.at)}</TableCell>
                      <TableCell className="max-w-[14rem]">
                        <div className="font-medium truncate">{r.name || r.email || "–"}</div>
                        <div className="text-xs text-muted-foreground truncate">{r.email || "–"}</div>
                      </TableCell>
                      <TableCell>{r.grade != null ? <Badge variant="outline" className="text-[10px] py-0 h-4">G{r.grade}</Badge> : "–"}</TableCell>
                      <TableCell className="capitalize text-sm">{r.plan || "–"}</TableCell>
                      <TableCell className="max-w-[16rem]">
                        <div className="text-sm truncate">{r.reason || "Not given"}</div>
                        {r.details ? <div className="text-xs text-muted-foreground truncate">{r.details}</div> : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-destructive">{formatMoney(r.mrrLost)}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.months}</TableCell>
                      <TableCell>
                        {r.offerAccepted ? <Badge className="border-0 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">Saved</Badge>
                          : r.offerShown ? <Badge variant="outline" className="font-normal">Shown</Badge>
                            : <span className="text-xs text-muted-foreground">–</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.userId ? (
                          <Link href={`/admin/users?openUser=${r.userId}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                            Open user <ExternalLink className="w-3 h-3" />
                          </Link>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
